import React, { useEffect, useState } from "react";
import { ProjectRecord } from "@/types";
import { FormProvider, useForm } from "react-hook-form";
import Modal from "@/components/Modal";
import { StyledRightActionButton } from "@/components/styled";
import SvgUserEdit from "@/assets/icons/SVGUserEdit.tsx";
import { AddProjectForm } from "@/pages/ProjectDetailsPage/components/AddProjectModal.tsx";
import {
  updateProject,
  validateAccountNumber,
  getProjectReq,
} from "@/api/projectsApi";
import { updateContractorProjectReq } from "@/api/contractorApi";
import { getUserReq } from "@/api/adminApi";
import { getBatteryOemsReq } from "@/api/userApi";
import { safeString } from "@/utils/stringUtils";
import { removeTrailZero } from "@/utils/numberFormatting";
import {
  PROJECT_ALREADY_EXISTS_ERROR_MESSAGE,
  PROJECT_STEPS,
  PROJECT_STEP_CONFIGS,
  ProjectStep,
} from "@/constants";
import { Box, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import { validateCustomerName } from "@/utils/validateCustomerName.ts";
import { useErrorBoundary } from "@/libs/ErrorBoundary.tsx";
import Stepper from "@/components/Stepper";
import MultiStepProjectForm from "@/pages/ProjectDetailsPage/components/MultiStepProjectForm.tsx";
import { useAuthStore } from "@/store/authStore.ts";
import { useUserStore } from "@/store/userStore.ts";
import { parseStreetAddress } from "@/utils/addressUtils";
import { appendSolarFields } from "@/utils/appendSolarFields";
import dayjs from "dayjs";

interface ProjectApiResponse extends ProjectRecord {
  batteryModelId?: number | string;
  batteryOemId?: number | string;
  totalCapacity?: string;
  total_cost?: string | number;
  extra_incentives?: string | number;
  externalIncentives?: string | number;
  externalIncentivePrograms?: string;
  thirdPartyOwned?: boolean;
  inverterManufacturer?: string;
  applicantState?: string;
  state?: string;
  payee?: string;
  payeeType?: string;
  nominalPowerkw?: string;
  ptoNameplateKw?: string;
  Account_No?: string;
  accountNumber?: string;
  Mailing_Address?: string;
  Mailing_City?: string;
  Mailing_State?: string;
  Mailing_Zip_Code?: string;
  mailingAddress?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZipCode?: string;
  Customer_First_Name?: string;
  Customer_Last_Name?: string;
  payeeName?: string;
  payeeEmail?: string;
  sdcpProject?: {
    NEM_SBP?: string;
    CARE?: string;
    FERA?: string;
    isCoC?: string;
    State?: string;
  };
}

type Props = {
  project: ProjectApiResponse;
  iconOnly?: boolean;
  getUpdatedProjects: () => void;
  getFilesReq?: () => void;
};

const EditProjectModal = ({
  project,
  getUpdatedProjects,
  getFilesReq,
  iconOnly = false,
}: Props) => {
  const handleError = useErrorBoundary();
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const role = useAuthStore((state) => state.role);
  const user = useUserStore((state) => state.user);
  const isContractor = role === "contractor";
  const isAdmin = role === "administrator";
  const [open, setOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [fullProjectData, setFullProjectData] = useState<ProjectRecord | null>(
    null
  );
  const [isLoadingProjectData, setIsLoadingProjectData] = useState(false);

  const [step, setStep] = useState<ProjectStep>(PROJECT_STEPS.SYSTEM_INFO);

  const methods = useForm<Partial<AddProjectForm>>({
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    setValue,
    setError,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    const initializeProjectData = async () => {
      if (!project || !open) return;

      const completeProject = await fetchCompleteProjectData(project.id);
      const data = mapProjectToFormData(completeProject);

      if (data.batteryManufacturer && !data.batteryOemId) {
        try {
          const response = await getBatteryOemsReq("");
          const matchingOem = response?.data?.oems?.find(
            (oem: { id: number; name: string }) =>
              oem.name === data.batteryManufacturer
          );
          if (matchingOem?.id) {
            data.batteryOemId = String(matchingOem.id);
          }
        } catch (error) {
          console.error("Failed to fetch battery OEM ID:", error);
        }
      }

      reset(data);
    };

    if (open) {
      initializeProjectData();
    }
  }, [project, reset, open]);

  const populatePayeeInfoFromAccount = async (
    accountHash: string,
    methods: ReturnType<typeof useForm<Partial<AddProjectForm>>>
  ) => {
    try {
      const mailingResponse = await validateAccountNumber(accountHash);
      const mailingAddressData = mailingResponse.data?.dataValues || {};

      // Set address fields if they exist
      const addressFields = {
        payeeAddress: mailingAddressData.Mailing_Address,
        payeeCity: mailingAddressData.Mailing_City,
        payeeState: mailingAddressData.Mailing_State,
        payeeZip: mailingAddressData.Mailing_Zip_Code,
      };

      Object.entries(addressFields).forEach(([field, value]) => {
        if (value) {
          methods.setValue(field as keyof AddProjectForm, value);
        }
      });

      if (
        mailingAddressData.Customer_First_Name &&
        mailingAddressData.Customer_Last_Name
      ) {
        const fullName =
          `${mailingAddressData.Customer_First_Name} ${mailingAddressData.Customer_Last_Name}`.trim();
        methods.setValue("payeeName", fullName);
      }

      const email = mailingAddressData.email || mailingAddressData.accountEmail;
      if (email) {
        methods.setValue("accountEmail", email);
      }
    } catch (mailingError) {
      console.warn("Could not fetch mailing address data:", mailingError);
    }
  };

  const handleStepChange = async (newStep: ProjectStep) => {
    if (newStep === PROJECT_STEPS.SYSTEM_INFO) {
      const customerName = methods.getValues("customerName");
      const accountHash = methods.getValues("accountHash");
      if (customerName !== project?.customerName) {
        const customerNameError = await validateCustomerName(
          customerName ?? ""
        );
        if (customerNameError !== true) {
          methods.setError("customerName", { message: customerNameError });
          return;
        }
      }
      if (accountHash && accountHash !== project?.accountHash) {
        try {
          await validateAccountNumber(accountHash);
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Account number validation failed. Please check and try again.";
          methods.setError("accountHash", { message: errorMessage });
          return;
        }
      }
    }

    if (newStep === PROJECT_STEPS.PAYEE_INFO) {
      const accountHash = methods.getValues("accountHash");
      if (accountHash) {
        await populatePayeeInfoFromAccount(accountHash, methods);
      }
    }

    setStep(newStep);
  };

  const fetchCompleteProjectData = async (projectId: number) => {
    setIsLoadingProjectData(true);
    try {
      // Fetch main project data
      const response = await getProjectReq(projectId);

      const completeProjectData = response.data.project; // Access nested project object

      // Fetch mailing address data if account number is available
      let mailingAddressData = {};
      const accountNumber =
        completeProjectData.accountHash ||
        completeProjectData.Account_No ||
        completeProjectData.accountNumber;
      if (accountNumber) {
        try {
          const mailingResponse = await validateAccountNumber(accountNumber);
          mailingAddressData = mailingResponse.data?.dataValues || {};
        } catch (mailingError) {
          console.warn("Could not fetch mailing address data:", mailingError);
        }
      }

      const mergedData = { ...completeProjectData, ...mailingAddressData };
      setFullProjectData(mergedData);
      return mergedData;
    } catch (error) {
      console.error("Error fetching project data:", error);
      setFullProjectData(project);
      return project;
    } finally {
      setIsLoadingProjectData(false);
    }
  };

  const mapProjectToFormData = (project: ProjectApiResponse) => {
    const solarSystemTypes: string[] = [];
    if (project.solarKwdc && Number(project.solarKwdc) > 0) {
      solarSystemTypes.push("existing");
    }
    if (project.newSolarKwdc && Number(project.newSolarKwdc) > 0) {
      solarSystemTypes.push("new");
    }

    if (solarSystemTypes.length === 0) {
      solarSystemTypes.push("existing");
    }

    const data = {
      ...project,
      contractor: project.contractor || "",
      contractorId: project.contractorId ? String(project.contractorId) : "",
      batteryManufacturer: project.batteryOem || "",
      batteryOemId: project.batteryOemId
        ? String(project.batteryOemId)
        : project.oemId
        ? String(project.oemId)
        : "",
      batteryProductName: project.batteryModel || "",
      batteryModelId: project.batteryModelId
        ? String(project.batteryModelId)
        : "",
      usableCapacity: removeTrailZero(safeString(project.usableCapacity)),
      usableCapacityIncentive: removeTrailZero(
        safeString(project.usableCapacityIncentive)
      ),
      batteryQuantity: project.numberOfBatteries || "",
      totalCapacity: removeTrailZero(
        project.totalCapacity || ""
      ),
      nominalPower: removeTrailZero(
        project.nominalPowerkw || project.ptoNameplateKw || ""
      ),
      upfrontIncentivePayment: project.upfrontIncentivePayment
        ? `${Number(project.upfrontIncentivePayment) / 100}`
        : "",
      totalProjectCost: safeString(project.total_cost),
      externalIncentives: safeString(
        project.externalIncentives || project.extra_incentives
      ),
      externalIncentivePrograms: project.externalIncentivePrograms || "",
      thirdPartyOwned:
        project.thirdPartyOwned || project.thirdPartyOwned || false,
      solarSystemType: solarSystemTypes,
      streetAddress: project.address ? parseStreetAddress(project.address) : "",
      city: project.city || "",
      zipCode: project.zipCode || "",
      state: project.applicantState || project.state || "",

      solarKwdc: removeTrailZero(project.solarKwdc || ""),
      solarKwac: removeTrailZero(project.solarKwac || ""),
      solarAnnualProduction: removeTrailZero(
        project.solarAnnualProduction || ""
      ),
      newSolarKwdc: removeTrailZero(project.newSolarKwdc || ""),
      newSolarKwac: removeTrailZero(project.newSolarKwac || ""),
      newSolarAnnualProduction: removeTrailZero(
        project.newSolarAnnualProduction || ""
      ),
      inverterName: project.existingInverter || "",
      inverterManufacturer: project.inverterManufacturer || "",

      operationalizedDate: project.operationalizedDate
        ? dayjs(project.operationalizedDate).format("YYYY-MM-DDTHH:mm:ss")
        : null,

      estimated_installation_date: project.estimated_installation_date
        ? dayjs(project.estimated_installation_date).format("YYYY-MM-DDTHH:mm:ss")
        : null,

      payeeType: project.payeeType || "",
      payeeName:
        project.payeeName ||
        (project.Customer_First_Name && project.Customer_Last_Name
          ? `${project.Customer_First_Name} ${project.Customer_Last_Name}`.trim()
          : ""),
      accountEmail: project.payeeEmail || project.accountEmail || "",

      // Mailing address fields - mapping to correct form field names
      payeeAddress:
        project.mailingAddress ||
        project.Mailing_Address ||
        project.address ||
        "",
      payeeCity:
        project.mailingCity || project.Mailing_City || project.city || "",
      payeeState:
        project.mailingState ||
        project.Mailing_State ||
        project.applicantState ||
        "",
      payeeZip:
        project.mailingZipCode ||
        project.Mailing_Zip_Code ||
        project.zipCode ||
        "",

      careEligible:
        project.sdcpProject?.CARE === "Y" || project.careEligible || false,
      feraEligible:
        project.sdcpProject?.FERA === "Y" || project.feraEligible || false,
      cocEligible:
        project.sdcpProject?.isCoC === "Y" || project.cocEligible || false,
    } as unknown as AddProjectForm & {
      extendToDate?: string;
      extendToDateNote?: string;
    };

    const projectExtensions = project.ProjectExtensions ?? [];
    if (projectExtensions.length > 0) {
      data.extendToDate =
        projectExtensions[projectExtensions.length - 1].extendToDate;
      data.extendToDateNote =
        projectExtensions[projectExtensions.length - 1].note;
    }

    return data;
  };

  const getChangedValues = (
    formData: Partial<AddProjectForm>,
    initialData: Partial<AddProjectForm>
  ): Partial<AddProjectForm> => {
    const changedValues: Record<string, any> = {};
    Object.keys(formData).forEach((key) => {
      const formValue = formData[key as keyof AddProjectForm];
      const initialValue = (initialData as any)[key];

      if (formValue !== initialValue) {
        changedValues[key] = formValue;
      }
    });
    return changedValues as Partial<AddProjectForm>;
  };

  const handleClose = () => {
    setOpen(false);

    const projectData = fullProjectData || project;
    const data = mapProjectToFormData(projectData);

    reset(data);
    setStep(PROJECT_STEPS.SYSTEM_INFO);
  };

  const handleFormChange = () => {
    const formData = getValues();
    const hasAnyChanges = Object.keys(formData).some(
      (key) =>
        formData[key as keyof AddProjectForm] !==
        project?.[key as keyof ProjectRecord]
    );
    setHasChanges(hasAnyChanges);
  };

  const onSubmit = async (data: Partial<AddProjectForm>) => {
    try {
      const mappedProjectData = mapProjectToFormData(project);
      const changedValues = getChangedValues(data, mappedProjectData);

      const formData = new FormData();

      const appendIfValue = (key: string, value: any) => {
        if (value !== undefined && value !== null) {
          const stringValue = String(value);
          if (stringValue !== "") {
            formData.append(key, stringValue);
          }
        }
      };

      const appendIfChanged = (key: string, value: any) => {
        if (key in changedValues) {
          formData.append(key, String(value || ""));
        }
      };

      appendIfChanged("customerName", data.customerName);
      appendIfChanged("accountHash", data.accountHash);
      appendIfChanged("zipCode", data.zipCode);
      appendIfChanged("city", data.city);
      appendIfChanged("phoneNumber", data.phoneNumber);

      const addressFields = ["streetAddress", "city", "state", "zipCode"];
      const hasAddressChanges = addressFields.some(
        (field) => field in changedValues
      );

      if (hasAddressChanges) {
        const address = [
          data.streetAddress,
          data.city,
          data.state,
          data.zipCode,
        ]
          .filter(Boolean)
          .join(", ");

        if (address) {
          formData.append("address", address);
        }
      }

      appendIfChanged("accountEmail", data.accountEmail);
      appendIfChanged("contractor", data.contractor);
      appendIfChanged("contractorId", data.contractorId);

      appendIfValue("batteryOem", data.batteryManufacturer);
      appendIfValue("batteryModel", data.batteryProductName);
      
      // Include OEM and battery model IDs for proper foreign key relationships
      appendIfValue("oemId", data.batteryOemId);
      appendIfValue("batteryModelId", data.batteryModelId);
      
      appendIfValue("usableCapacity", data.usableCapacity);
      appendIfValue("usableCapacityIncentive", data.usableCapacityIncentive);
      appendIfValue("numberOfBatteries", data.batteryQuantity);

      // SDCP-1305: upfrontIncentivePayment is deliberately NOT sent. Saving a project must
      // never restate an already-approved incentive — the amount only changes through the
      // recalculate / edit-incentive controls on the project details page. The API drops the
      // field on this route too, so both ends are locked.

      const solarSystemTypes = Array.isArray(data.solarSystemType)
        ? data.solarSystemType
        : [];
      const hasExistingSolar = solarSystemTypes.includes("existing");
      const hasNewSolar = solarSystemTypes.includes("new");

      formData.append("hasExistingSolar", String(hasExistingSolar));
      formData.append("hasNewSolar", String(hasNewSolar));

      appendSolarFields(
        formData,
        {
          solarKwdc: data.solarKwdc,
          solarKwac: data.solarKwac,
          solarAnnualProduction: data.solarAnnualProduction,
          newSolarKwdc: data.newSolarKwdc,
          newSolarKwac: data.newSolarKwac,
          newSolarAnnualProduction: data.newSolarAnnualProduction,
        },
        { hasExistingSolar, hasNewSolar }
      );

      appendIfValue("total_cost", data.totalProjectCost);
      appendIfValue("extra_incentives", data.externalIncentives);
      appendIfValue("thirdPartyOwned", data.thirdPartyOwned);
      appendIfValue(
        "externalIncentivePrograms",
        data.externalIncentivePrograms
      );
      appendIfValue("existingInverter", data.inverterName);
      appendIfValue("inverterManufacturer", data.inverterManufacturer);

      appendIfChanged("operationalizedDate", data.operationalizedDate);
      appendIfChanged("estimated_installation_date", data.estimated_installation_date);

      if (data.participantEnrollment instanceof File) {
        formData.append("participantEnrollment", data.participantEnrollment);
      }
      if (data.systemSizingTool instanceof File) {
        formData.append("systemSizingTool", data.systemSizingTool);
      }
      if (data.contract instanceof File) {
        formData.append("contract", data.contract);
      }
      
      // Automatically set termsAccepted to true if OEM requires terms
      // OEMs requiring terms: Tesla, Enphase, Fortress, Panasonic
      const oemsRequiringTerms = ['Tesla', 'Enphase', 'Fortress', 'Panasonic'];
      const batteryManufacturer = data.batteryManufacturer || '';
      const requiresTerms = oemsRequiringTerms.some(oem => 
        batteryManufacturer.toLowerCase().includes(oem.toLowerCase())
      );
      
      if (requiresTerms) {
        formData.append("termsAccepted", "true");
      }
      
      if (data.termsAndConditionsFile instanceof File) {
        formData.append("termsAndConditionsFile", data.termsAndConditionsFile);
      }

      appendIfValue("payeeName", data.payeeName);
      appendIfValue("payeeType", data.payeeType);

      appendIfValue("mailingAddress", data.payeeAddress);
      appendIfValue("mailingCity", data.payeeCity);
      appendIfValue("mailingState", data.payeeState);
      appendIfValue("mailingZipCode", data.payeeZip);

      let payeeEmail = data.accountEmail;
      if (data.payeeType === "contractor" && data.contractorId) {
        try {
          const contractorResponse = await getUserReq(
            Number(data.contractorId)
          );
          const contractorEmail = contractorResponse?.data?.userData?.email;
          payeeEmail = contractorEmail || data.accountEmail;
        } catch (error) {
          console.warn("Could not fetch contractor email:", error);
          payeeEmail = data.accountEmail;
        }
      } else if (data.payeeType === "customer" || data.payeeType === "admin") {
        payeeEmail = data.accountEmail;
      }
      appendIfValue("payeeMailAdr", payeeEmail);

      if (isContractor) {
        await updateContractorProjectReq(project?.id, formData);
      } else {
        await updateProject(project?.id, formData);
      }

      getUpdatedProjects();

      if (getFilesReq) {
        getFilesReq();
      }

      handleClose();
    } catch (e: any) {
      console.error("Error in EditProjectModal onSubmit:", e);
      const errorMessage = e?.response?.data?.error;

      const isDuplicateAccountError =
        errorMessage === PROJECT_ALREADY_EXISTS_ERROR_MESSAGE ||
        errorMessage?.toLowerCase().includes("account number already exists");

      if (isDuplicateAccountError) {
        methods.setError("accountHash", {
          message: errorMessage,
          type: "manual",
        });
        setStep(PROJECT_STEPS.PERSONAL_INFO);
      } else if (errorMessage === "Project already exists") {
        console.error("Server error: Project already exists during update");
        handleError(
          new Error(
            "Failed to update project. The server is incorrectly treating this as a duplicate project. Please contact support."
          )
        );
      } else {
        handleError(e);
        console.error(e);
      }
    }
  };

  useEffect(() => {
    const subscription = watch(handleFormChange);
    return () => subscription.unsubscribe();
  }, [watch, project]);

  const isLoading = isLoadingProjectData;
  const isEditDisabled = !isAdmin && project?.sizing_passed === 1;

  return (
    <>
      <StyledRightActionButton
        // Icon-only on desktop, so there is no accessible text to target from tests.
        data-testid="edit-project-button"
        aria-label="Edit project"
        sx={{
          gap: "6px",
          whiteSpace: "nowrap",
          minWidth: "initial",
          minHeight: "initial",
          flex: 1,
          ...(!downSm && { border: 0 }),
          ...(isEditDisabled && {
            opacity: 0.5,
            cursor: "not-allowed",
          }),
        }}
        onClick={() => !isEditDisabled && setOpen(true)}
        variant={iconOnly ? "text" : downSm ? "outlined" : "text"}
        disabled={isEditDisabled}
      >
        {!downSm && <SvgUserEdit />}
        {iconOnly && downSm && <SvgUserEdit />}
        {downSm && !iconOnly && "Edit project"}
      </StyledRightActionButton>
      <Modal open={open} onClose={handleClose} title={"Edit project"}>
        <>
          <Box sx={{ mt: 3, mb: 4, position: "relative", px: 0 }}>
            <Stepper steps={PROJECT_STEP_CONFIGS} currentStep={step} />
          </Box>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <FormProvider {...methods}>
              <MultiStepProjectForm
                handleSubmit={handleSubmit(onSubmit)}
                handleClose={handleClose}
                handleStep={handleStepChange}
                step={step}
                mode="edit"
                projectId={project?.id}
              />
            </FormProvider>
          )}
        </>
      </Modal>
    </>
  );
};

export default EditProjectModal;
